// Helpers compartilhados do módulo fiscal (BDM-887).

export const DOCUMENT_MODELS = [
    { label: 'NF-e (produto)', value: 'nfe' },
    { label: 'NFS-e (serviço)', value: 'nfse' },
];

export const DOCUMENT_STATUSES = [
    // A nota é gerada e fica aqui até alguém mandar emitir.
    { label: 'Pendente', value: 'draft' },
    // "Enviando", não "Na fila": fila é jargão de infraestrutura — para quem
    // emite, a nota está a caminho do fisco.
    { label: 'Enviando', value: 'queued' },
    { label: 'Processando', value: 'processing' },
    { label: 'Autorizada', value: 'authorized' },
    { label: 'Recusada', value: 'rejected' },
    { label: 'Cancelada', value: 'canceled' },
    { label: 'Negada', value: 'denied' },
    { label: 'Erro', value: 'error' },
];

// Status que ainda vão mudar sozinhos — a tela fica atualizando enquanto isso.
export const PENDING_STATUSES = ['queued', 'processing'];

export const getModelLabel = model =>
    DOCUMENT_MODELS.find(option => option.value === model)?.label ?? model;

export const getStatusLabel = status =>
    DOCUMENT_STATUSES.find(option => option.value === status)?.label ?? status;

export const TAX_REGIMES = [
    { label: 'Simples Nacional', value: 'simplesNacional' },
    { label: 'Simples Nacional — excesso de sublimite', value: 'simplesNacionalExcess' },
    { label: 'Lucro Presumido', value: 'lucroPresumido' },
    { label: 'Lucro Real', value: 'lucroReal' },
];

// Regime especial de tributação do ISSQN (C15 da NFS-e): é um enquadramento
// concedido pela PREFEITURA, não o regime federal acima. A maioria das
// empresas não tem nenhum — por isso o padrão é 0.
export const SPECIAL_TAX_REGIMES = [
    { label: 'Sem regime especial', value: '0' },
    { label: 'Microempresa municipal', value: '1' },
    { label: 'Estimativa', value: '2' },
    { label: 'Sociedade de profissionais', value: '3' },
    { label: 'Cooperativa', value: '4' },
    { label: 'Microempreendedor individual (MEI)', value: '5' },
    { label: 'Microempresa ou empresa de pequeno porte (ME/EPP)', value: '6' },
    { label: 'Lucro Real', value: '7' },
    { label: 'Lucro Presumido', value: '8' },
    { label: 'Tributação normal', value: '9' },
    { label: 'Simples Nacional com excesso de sublimite', value: '10' },
    { label: 'Empresa de responsabilidade limitada', value: '11' },
];

// Regimes fora do Simples usam CST (e não CSOSN). O valor legado
// "regimeNormal" (fase anterior) conta como Lucro Presumido.
export const isSimplesRegime = regime =>
    !['regimeNormal', 'lucroPresumido', 'lucroReal'].includes(regime ?? 'simplesNacional');

// Operação não onerosa (remessa, comodato, doação): a NF-e exige o grupo de
// pagamento, e é o meio 90 que diz que não houve pagamento nenhum. As demais
// formas vêm do cadastro (BDM-1208).
export const PAYMENT_NONE = '90';

// Finalidades que saem sempre sem pagamento (meio 90): a devolução por
// exigência da SEFAZ, a remessa e a bonificação porque não há o que receber.
// O backend aplica a mesma regra (FiscalOperation::withoutPayment), então a
// tela trava a forma em vez de só sugerir.
export const UNPAID_PURPOSES = ['return', 'shipment', 'bonus'];

// Indicador de presença do comprador (B11b da NF-e). O 4 não aparece:
// é exclusivo de NFC-e (entrega a domicílio).
export const PRESENCE_INDICATOR_OPTIONS = [
    { label: '9 — Operação não presencial, outros', value: '9' },
    { label: '1 — Operação presencial', value: '1' },
    { label: '2 — Operação não presencial, pela Internet', value: '2' },
    { label: '3 — Operação não presencial, Teleatendimento', value: '3' },
    { label: '5 — Operação presencial, fora do estabelecimento', value: '5' },
    { label: '0 — Não se aplica (por exemplo, nota complementar ou de ajuste)', value: '0' },
];

// Origem da mercadoria — Anexo I do MOC 7.00 (campo N11), texto da tabela.
// Reescrever "em linguagem de quem revende" já tinha apagado a faixa de 40 a 70%
// do código 3 (o que o separa do 8) e o "e gás natural" dos códigos 6 e 7.
//
// Esta tabela, o CSOSN, o CST do ICMS, o motivo de desoneração, o indicador de
// presença e a exigibilidade do ISSQN foram conferidos em 05/09/2026 contra o
// Anexo I do MOC 7.00 (moc7-anexo-i-leiaute-e-rv.pdf, portal da SVRS).
export const FISCAL_ORIGINS = [
    { label: '0 — Nacional, exceto as indicadas nos códigos 3, 4, 5 e 8', value: '0' },
    { label: '1 — Estrangeira - Importação direta, exceto a indicada no código 6', value: '1' },
    { label: '2 — Estrangeira - Adquirida no mercado interno, exceto a indicada no código 7', value: '2' },
    { label: '3 — Nacional, mercadoria ou bem com Conteúdo de Importação superior a 40% e inferior ou igual a 70%', value: '3' },
    { label: '4 — Nacional, cuja produção tenha sido feita em conformidade com os processos produtivos básicos', value: '4' },
    { label: '5 — Nacional, mercadoria ou bem com Conteúdo de Importação inferior ou igual a 40%', value: '5' },
    { label: '6 — Estrangeira - Importação direta, sem similar nacional, constante em lista da CAMEX e gás natural', value: '6' },
    { label: '7 — Estrangeira - Adquirida no mercado interno, sem similar nacional, constante lista CAMEX e gás natural', value: '7' },
    { label: '8 — Nacional, mercadoria ou bem com Conteúdo de Importação superior a 70%', value: '8' },
];

// CSOSN — tabela oficial do Simples Nacional.
export const CSOSN_OPTIONS = [
    { label: '101 — Tributada pelo Simples Nacional com permissão de crédito', value: '101' },
    { label: '102 — Tributada pelo Simples Nacional sem permissão de crédito', value: '102' },
    { label: '103 — Isenção do ICMS no Simples Nacional para faixa de receita bruta', value: '103' },
    { label: '201 — Tributada pelo Simples Nacional com permissão de crédito e com cobrança do ICMS por Substituição Tributária', value: '201' },
    { label: '202 — Tributada pelo Simples Nacional sem permissão de crédito e com cobrança do ICMS por Substituição Tributária', value: '202' },
    { label: '203 — Isenção do ICMS no Simples Nacional para faixa de receita bruta e com cobrança do ICMS por Substituição Tributária', value: '203' },
    { label: '300 — Imune', value: '300' },
    { label: '400 — Não tributada pelo Simples Nacional', value: '400' },
    { label: '500 — ICMS cobrado anteriormente por substituição tributária (substituído) ou por antecipação', value: '500' },
    { label: '900 — Outros', value: '900' },
];

// CSOSN 101/201 dão crédito de ICMS ao cliente e exigem a alíquota de
// crédito na nota; o 900 exige a alíquota de ICMS. Sem elas o provedor
// recusa a nota na validação.
export const CSOSN_WITH_CREDIT = ['101', '201'];
export const csosnNeedsCreditRate = csosn => CSOSN_WITH_CREDIT.includes(csosn);
export const csosnNeedsIcmsRate = csosn => csosn === '900';

export const CST_OPTIONS = [
    { label: '00 — Tributada integralmente', value: '00' },
    { label: '10 — Tributada e com cobrança do ICMS por substituição tributária', value: '10' },
    { label: '20 — Com redução de base de cálculo', value: '20' },
    { label: '30 — Isenta ou não tributada e com cobrança do ICMS por substituição tributária', value: '30' },
    { label: '40 — Isenta', value: '40' },
    { label: '41 — Não tributada', value: '41' },
    { label: '50 — Suspensão', value: '50' },
    { label: '51 — Diferimento', value: '51' },
    { label: '60 — ICMS cobrado anteriormente por substituição tributária', value: '60' },
    { label: '70 — Com redução de base de cálculo e cobrança do ICMS por substituição tributária', value: '70' },
    { label: '90 — Outros', value: '90' },
];

// O CFOP tem 4 dígitos e o mercado lê com o ponto do milhar (5.102). O ponto é
// só leitura: o estado guarda dígitos, senão o parseInt do envio corta nele.
export const formatCfop = value => {
    const digits = String(value ?? '').replace(/\D/g, '').slice(0, 4);

    return digits.length === 4 ? `${digits[0]}.${digits.slice(1)}` : digits;
};

export const FISCAL_UNITS = [
    { label: 'UN — Unidade', value: 'UN' },
    { label: 'PC — Peça', value: 'PC' },
    { label: 'CX — Caixa', value: 'CX' },
    { label: 'KIT — Kit', value: 'KIT' },
    { label: 'PAR — Par', value: 'PAR' },
    { label: 'KG — Quilograma', value: 'KG' },
    { label: 'MT — Metro', value: 'MT' },
];

export const isPending = status => PENDING_STATUSES.includes(status);

// CST de PIS/COFINS (campos Q06 e S06 da NF-e). Os rótulos são os do Anexo I
// do MOC 7.00, conferidos em 05/09/2026 — antes misturavam o texto da EFD
// Contribuições, que descreve os mesmos códigos com outras palavras.

export const PIS_COFINS_CST_OPTIONS = [
    { label: '01 — Operação Tributável (base de cálculo = valor da operação, alíquota normal — cumulativo/não cumulativo)', value: '01' },
    { label: '02 — Operação Tributável (base de cálculo = valor da operação, alíquota diferenciada)', value: '02' },
    // O 03 (alíquota por unidade vendida) fica de fora: a emissão não monta
    // o grupo dele e o backend recusa a nota.
    { label: '04 — Operação Tributável (tributação monofásica, alíquota zero)', value: '04' },
    { label: '05 — Operação Tributável (Substituição Tributária)', value: '05' },
    { label: '06 — Operação Tributável (alíquota zero)', value: '06' },
    { label: '07 — Operação Isenta da Contribuição', value: '07' },
    { label: '08 — Operação Sem Incidência da Contribuição', value: '08' },
    { label: '09 — Operação com Suspensão da Contribuição', value: '09' },
    { label: '49 — Outras Operações de Saída', value: '49' },
];

// ————————————————————————————————————————————————
// Naturezas de operação (BDM-887)
// ————————————————————————————————————————————————

export const OPERATION_DIRECTIONS = [
    { label: 'Saída', value: 'out' },
    { label: 'Entrada', value: 'in' },
];

export const OPERATION_PURPOSES = [
    { label: 'Normal', value: 'normal' },
    { label: 'Devolução', value: 'return' },
    { label: 'Bonificação', value: 'bonus' },
    { label: 'Remessa', value: 'shipment' },
];

// null = deixa o sistema decidir pelo destinatário (pessoa física é
// consumidor final). O select usa string porque o InputSelect não guarda null.
export const FINAL_CONSUMER_OPTIONS = [
    { label: 'Decidir pelo cliente da nota', value: 'auto' },
    { label: 'Sim, sempre', value: 'yes' },
    { label: 'Não', value: 'no' },
];

// Abas da NATUREZA, na ordem em que aparecem. A natureza é do tenant e serve
// empresas de regimes diferentes, então as duas situações do ICMS convivem —
// e o rótulo diz qual é qual: "Simples" sozinho dava a entender que existe um
// imposto com esse nome. A antiga aba "Importação" saiu: ela nunca chegava ao
// payload da nota (auditoria fiscal — o grupo de II da NF-e nem tem CST) e
// configurá-la só criava falsa segurança.
export const OPERATION_TAXES = [
    { key: 'simples', label: 'ICMS (Simples)' },
    { key: 'icms', label: 'ICMS (Presumido/Real)' },
    { key: 'ipi', label: 'IPI' },
    { key: 'issqn', label: 'ISSQN' },
    { key: 'pis', label: 'PIS' },
    { key: 'cofins', label: 'COFINS' },
    { key: 'difal', label: 'DIFAL' },
    // IBS/CBS fica por último: é o grupo da transição, e o único sem nada a
    // digitar — a classificação se escolhe e os valores vêm da emissão.
    { key: 'ibscbs', label: 'IBS/CBS' },
];

/**
 * Abas de imposto do item da NOTA — outra lista, de propósito.
 *
 * Aqui o emitente já está definido, então CSOSN e CST não convivem: entra só
 * a situação do regime dele (a outra o backend descarta, e a aba só servia
 * para o usuário digitar no vazio). DIFAL, PIS e COFINS deixam de ser aba
 * própria e viram seção da aba a que pertencem — dez abas não cabiam sem
 * rolagem, e nenhuma das três é assunto separado do grupo em que vive.
 */
export const itemTaxTabs = isSimples => [
    {
        label: 'ICMS',
        taxes: [{ key: isSimples ? 'simples' : 'icms' }, { key: 'difal', title: 'DIFAL' }],
    },
    { label: 'IPI', taxes: [{ key: 'ipi' }] },
    {
        label: 'PIS/COFINS',
        taxes: [{ key: 'pis', title: 'PIS' }, { key: 'cofins', title: 'COFINS' }],
    },
    { label: 'ISSQN', taxes: [{ key: 'issqn' }] },
    { label: 'IBS/CBS', taxes: [{ key: 'ibscbs' }] },
];

// Impostos que podem ter regra própria por família de regime. Só PIS e
// COFINS por enquanto: o ICMS já resolve isso com CSOSN e CST convivendo
// como impostos distintos, e os demais não divergem por regime.
export const REGIME_TAXES = ['pis', 'cofins'];

// As duas famílias: quem usa CSOSN e quem usa CST. Não é o regime do
// cadastro da empresa (que tem quatro valores) — é o que muda a tributação.
export const RULE_REGIMES = [
    { key: 'simples', label: 'Simples Nacional' },
    { key: 'normal', label: 'Lucro Presumido / Lucro Real' },
];

export const getDirectionLabel = direction =>
    OPERATION_DIRECTIONS.find(option => option.value === direction)?.label ?? direction;

export const getPurposeLabel = purpose =>
    OPERATION_PURPOSES.find(option => option.value === purpose)?.label ?? purpose;

// CST do IPI. Entrada e saída têm tabelas distintas — mostrar as duas de uma
// vez faria o usuário escolher um código que a SEFAZ recusa na direção dele.
export const IPI_CST_OUT_OPTIONS = [
    { label: '50 — Saída tributada', value: '50' },
    { label: '51 — Saída tributada com alíquota zero', value: '51' },
    { label: '52 — Saída isenta', value: '52' },
    { label: '53 — Saída não tributada', value: '53' },
    { label: '54 — Saída imune', value: '54' },
    { label: '55 — Saída com suspensão', value: '55' },
    { label: '99 — Outras saídas', value: '99' },
];

export const IPI_CST_IN_OPTIONS = [
    { label: '00 — Entrada com recuperação de crédito', value: '00' },
    { label: '01 — Entrada tributada com alíquota zero', value: '01' },
    { label: '02 — Entrada isenta', value: '02' },
    { label: '03 — Entrada não tributada', value: '03' },
    { label: '04 — Entrada imune', value: '04' },
    { label: '05 — Entrada com suspensão', value: '05' },
    { label: '49 — Outras entradas', value: '49' },
];

// PIS/COFINS de entrada — na saída valem as opções de PIS_COFINS_CST_OPTIONS.
export const PIS_COFINS_CST_IN_OPTIONS = [
    { label: '50 — Operação com Direito a Crédito - Vinculada Exclusivamente a Receita Tributada no Mercado Interno', value: '50' },
    { label: '51 — Operação com Direito a Crédito - Vinculada Exclusivamente a Receita Não Tributada no Mercado Interno', value: '51' },
    { label: '52 — Operação com Direito a Crédito - Vinculada Exclusivamente a Receita de Exportação', value: '52' },
    { label: '53 — Operação com Direito a Crédito - Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno', value: '53' },
    { label: '54 — Operação com Direito a Crédito - Vinculada a Receitas Tributadas no Mercado Interno e de Exportação', value: '54' },
    { label: '55 — Operação com Direito a Crédito - Vinculada a Receitas Não-Tributadas no Mercado Interno e de Exportação', value: '55' },
    { label: '56 — Operação com Direito a Crédito - Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno, e de Exportação', value: '56' },
    { label: '60 — Crédito Presumido - Operação de Aquisição Vinculada Exclusivamente a Receita Tributada no Mercado Interno', value: '60' },
    { label: '61 — Crédito Presumido - Operação de Aquisição Vinculada Exclusivamente a Receita Não-Tributada no Mercado Interno', value: '61' },
    { label: '62 — Crédito Presumido - Operação de Aquisição Vinculada Exclusivamente a Receita de Exportação', value: '62' },
    { label: '63 — Crédito Presumido - Operação de Aquisição Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno', value: '63' },
    { label: '64 — Crédito Presumido - Operação de Aquisição Vinculada a Receitas Tributadas no Mercado Interno e de Exportação', value: '64' },
    { label: '65 — Crédito Presumido - Operação de Aquisição Vinculada a Receitas Não-Tributadas no Mercado Interno e de Exportação', value: '65' },
    { label: '66 — Crédito Presumido - Operação de Aquisição Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno, e de Exportação', value: '66' },
    { label: '67 — Crédito Presumido - Outras Operações', value: '67' },
    { label: '70 — Operação de Aquisição sem Direito a Crédito', value: '70' },
    { label: '71 — Operação de Aquisição com Isenção', value: '71' },
    { label: '72 — Operação de Aquisição com Suspensão', value: '72' },
    { label: '73 — Operação de Aquisição a Alíquota Zero', value: '73' },
    { label: '74 — Operação de Aquisição sem Incidência da Contribuição', value: '74' },
    { label: '75 — Operação de Aquisição por Substituição Tributária', value: '75' },
    { label: '98 — Outras Operações de Entrada', value: '98' },
    { label: '99 — Outras Operações', value: '99' },
];

// CST do IBS/CBS (Reforma Tributária, LC 214/2025) — tabela oficial com 18
// situações. O código completo do enquadramento é o cClassTrib, cujos três
// primeiros dígitos repetem o CST.
//
// Rótulos conferidos em 05/09/2026 contra o Portal da Conformidade Fiscal da
// SVRS (https://dfe-portal.svrs.rs.gov.br/CFF/ClassificacaoTributaria), a mesma
// fonte do FiscalTaxClassification no backend.
export const IBSCBS_CST_OPTIONS = [
    { label: '000 — Tributação integral', value: '000' },
    { label: '010 — Tributação com alíquotas uniformes', value: '010' },
    { label: '011 — Tributação com alíquotas uniformes reduzidas', value: '011' },
    { label: '200 — Alíquota reduzida', value: '200' },
    { label: '220 — Alíquota fixa', value: '220' },
    { label: '221 — Alíquota fixa proporcional', value: '221' },
    { label: '222 — Redução de base de cálculo', value: '222' },
    { label: '400 — Isenção', value: '400' },
    { label: '410 — Imunidade e não incidência', value: '410' },
    { label: '510 — Diferimento', value: '510' },
    { label: '515 — Diferimento com redução de alíquota', value: '515' },
    { label: '550 — Suspensão', value: '550' },
    { label: '620 — Tributação monofásica', value: '620' },
    { label: '800 — Transferência de crédito', value: '800' },
    { label: '810 — Ajuste de IBS na ZFM', value: '810' },
    { label: '811 — Ajustes', value: '811' },
    { label: '820 — Tributação em documento específico', value: '820' },
    { label: '830 — Exclusão da base de cálculo', value: '830' },
];

// Motivo da desoneração do ICMS (campo N28 da NF-e). Cada CST aceita só
// alguns — ver desonerationReasonsFor.
export const DESONERATION_REASONS = [
    { label: '1 — Táxi', value: '1' },
    { label: '3 — Produtor agropecuário', value: '3' },
    { label: '4 — Frotista/locadora', value: '4' },
    { label: '5 — Diplomático/consular', value: '5' },
    { label: '6 — Utilitários e motocicletas da Amazônia Ocidental e áreas de livre comércio', value: '6' },
    { label: '7 — SUFRAMA', value: '7' },
    { label: '8 — Venda a órgão público', value: '8' },
    { label: '9 — Outros', value: '9' },
    { label: '10 — Deficiente condutor', value: '10' },
    { label: '11 — Deficiente não condutor', value: '11' },
    { label: '12 — Órgão de fomento e desenvolvimento agropecuário', value: '12' },
    { label: '90 — Solicitado pelo Fisco', value: '90' },
];

// Motivos aceitos por CST, como no esquema do provedor (e na validação do
// backend): fora da lista, a nota é recusada.
const DESONERATION_REASONS_BY_CST = {
    '20': ['3', '9', '12'],
    '30': ['6', '7', '9'],
    '40': ['1', '3', '4', '5', '6', '7', '8', '9', '10', '11', '90'],
    '41': ['1', '3', '4', '5', '6', '7', '8', '9', '10', '11', '90'],
    '50': ['1', '3', '4', '5', '6', '7', '8', '9', '10', '11', '90'],
    '70': ['3', '9', '12'],
    '90': ['3', '9', '12'],
};

export const desonerationReasonsFor = cst => DESONERATION_REASONS.filter(
    reason => (DESONERATION_REASONS_BY_CST[cst] ?? []).includes(reason.value)
);

export const ISSQN_ENFORCEABILITY_OPTIONS = [
    { label: '1 — Exigível', value: '1' },
    { label: '2 — Não incidência', value: '2' },
    { label: '3 — Isenção', value: '3' },
    { label: '4 — Exportação', value: '4' },
    { label: '5 — Imunidade', value: '5' },
    { label: '6 — Exigibilidade suspensa por decisão judicial', value: '6' },
    { label: '7 — Exigibilidade suspensa por processo administrativo', value: '7' },
];

export const STATES = [
    'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS',
    'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC',
    'SE', 'SP', 'TO',
];

export const STATE_OPTIONS = STATES.map(state => ({ label: state, value: state }));

// Situações que levam o grupo de substituição tributária na nota. Fora
// delas, mostrar os campos de ST só confunde: eles não vão para a NF-e.
// 60 e 500 (ST retida antes) ficam de fora: o grupo deles informa o que foi
// retido na compra, e a emissão não envia (BDM-1312).
const CST_WITH_ST = ['10', '30', '70', '90'];
const CSOSN_WITH_ST = ['201', '202', '203', '900'];

export const needsSubstitution = (tax, cst) => tax === 'simples'
    ? CSOSN_WITH_ST.includes(cst)
    : CST_WITH_ST.includes(cst);

// Sem base própria na nota (CST 30, CSOSN 201–203), a alíquota é a do ICMS
// da operação própria, que a ST desconta — e por isso é obrigatória.
export const needsOwnRate = (tax, cst) => tax === 'simples'
    ? ['201', '202', '203'].includes(cst)
    : cst === '30';

export const needsDesoneration = cst => ['20', '30', '40', '41', '50', '70', '90'].includes(cst);

export const needsDeferral = cst => cst === '51';

// 40/41/50 não têm base nem alíquota próprias; 60 é imposto já cobrado.
export const icmsHasRate = cst => !['40', '41', '50', '60'].includes(cst);

// CST com FCP próprio na NF-e; nos demais o campo não vai para a nota.
export const icmsHasFcp = cst => ['00', '10', '20', '51', '70', '90'].includes(cst);
